// Segmentation (spec/dashboard.md section 8.1): cards over SmartObjects.SegmentOption with the type chip, the condition,
// the description, and the users badges of the selected environment with their definition; Refresh runs the worker's job.
// M7 slice 7: beside the sizes, the entered / left per day sparkline of the shared analytics range (the `segment` series).
import { useState, type ReactNode } from 'react';
import type { Job, Json } from '../../api/types';
import { ConditionSummary } from '../../content/conditions/ConditionBuilder';
import { CardList, type CardContext } from '../../content/cards/CardList';
import { Chip as FieldChip, paramOf } from '../../content/cards/Chips';
import { EnvSelector, useLiveOpsEnv } from '../../content/liveops/env';
import { n, useDefinitionText, useEntityFigures } from '../../content/liveops/analytics';
import { useSegmentSizes, type SegmentSizes } from '../../content/liveops/stats';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { Filter, RefreshCw, User, Users } from '../../ui/icons';
import { CommitTextInput } from '../../ui/inputs';
import { JobProgress } from '../../ui/JobProgress';
import { useToasts } from '../../ui/Toast';
import { Sparkline } from '../../ui/Sparkline';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { BodyField, CommonTail, NameText, paramsOf } from './cards';
import { RangePicker } from './RangePicker';
import './liveops.css';

export const SEGMENT = 'SmartObjects.SegmentOption';

const TYPE_HELP: Record<number, string> = {
  0: 'Default: the player leaves the segment when its condition stops passing',
  1: 'CantLeave: once in, the player stays in the segment forever',
};

export function SegmentsPage() {
  const { env } = useLiveOpsEnv();
  const sizes = useSegmentSizes(env);
  return (
    <CardList
      id="segments"
      title="Segmentation"
      template={SEGMENT}
      includeHeirs={false}
      toolbar={<SizesToolbar sizes={sizes} />}
      header={(ctx) => <SegmentHeader ctx={ctx} sizes={sizes.data} />}
      body={(ctx) => <SegmentBody ctx={ctx} />}
      filterFields={[{ key: 'data.type', label: 'Type', type: 'enum', group: 'Segment', options: [{ value: '0', label: 'Default' }, { value: '1', label: 'CantLeave' }] }]}
      emptyText="No segments yet. A segment is a condition the engine keeps evaluating; the players in it can gate events, offers and store slots."
      emptyIcon={<Users aria-hidden="true" />}
    />
  );
}

function SizesToolbar({ sizes }: { sizes: ReturnType<typeof useSegmentSizes> }) {
  const { can } = useGame();
  const { client } = useAuth();
  const { org, game } = useGame();
  const toasts = useToasts();
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  const [job, setJob] = useState<Job | null>(null);
  const canRefresh = can('content_write');
  const refresh = async () => {
    try {
      setJob(await sizes.refresh());
    } catch (e) {
      toasts.error(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <div className="lo-sizes" data-testid="segment-sizes">
      <EnvSelector />
      <RangePicker />
      <span className="lo-sizes-text" title="total = players whose current profile has isIn = true for the segment; month / week = those seen in the last 30 / 7 days">
        {sizes.loading && !sizes.data ? 'loading sizes…' : sizes.error ? `sizes unavailable (${sizes.error.message})` : sizes.data?.computedAt ? `${sizes.data.players} players with a profile · computed ${formatInstantWithZone(sizes.data.computedAt, zone, { offsets })}` : 'sizes not computed yet'}
      </span>
      {job ? (
        <JobProgress
          job={job}
          api={{ client, org, game: game.slug, missingPermission: canRefresh ? null : 'content_write' }}
          onDone={(j) => {
            setJob(null);
            if (j.status === 'succeeded') {
              toasts.success('Segment sizes recomputed.');
              sizes.reload();
            } else toasts.error(`Refresh ${j.status}${j.error ? `: ${(j.error as { message?: string }).message ?? JSON.stringify(j.error)}` : ''}`);
          }}
        />
      ) : (
        <Button size="s" onClick={() => void refresh()} disabled={!canRefresh} missingPermission={canRefresh ? undefined : 'content_write'} title="Recompute the sizes of this environment now (the worker also recomputes them hourly)" icon={<RefreshCw aria-hidden="true" />}>
          Refresh
        </Button>
      )}
    </div>
  );
}

/** A titled group of the expanded body; `single` hides the lone field's label visually. */
function Group({ title, single, children }: { title: string; single?: boolean; children: ReactNode }) {
  return (
    <section className={`lo-group ${single ? 'single' : ''}`} aria-label={title}>
      <h4 className="lo-group-title">{title}</h4>
      {children}
    </section>
  );
}

function SegmentHeader({ ctx, sizes }: { ctx: CardContext; sizes: SegmentSizes | null }) {
  const params = paramsOf(SEGMENT);
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  const d = (ctx.doc.data ?? {}) as Record<string, Json>;
  const type = paramOf(params, 'type');
  const row = sizes?.items.find((i) => i.segmentId === ctx.doc.id);
  return (
    <div className="card-head-lines">
      <div className="lo-head-line">
        <NameText doc={ctx.doc} />
        <span className="lo-size-badge">
          <Badge tone={row ? 'info' : 'neutral'} title={row ? `${row.total} players in the segment; ${row.month} of them seen in the last 30 days, ${row.week} in the last 7 (of ${sizes?.players ?? '?'} players with a profile; computed ${sizes?.computedAt ? formatInstantWithZone(sizes.computedAt, zone, { offsets }) : '—'})` : 'No size recorded for this segment in the selected environment (nobody evaluated it, or not computed yet)'}>
          <User className="icon-s" aria-hidden="true" /> {row ? `${row.total} · ${row.month} · ${row.week}` : '—'}
          </Badge>
        </span>
        <SegmentFlow id={ctx.doc.id} />
        <span className="lo-cond">
          <Filter aria-hidden="true" />
          <ConditionSummary rootId={typeof d.condition === 'string' ? d.condition : null} />
        </span>
      </div>
      <div className="card-chips">
        {type && (
          <span title={TYPE_HELP[Number(d.type ?? 0)] ?? ''}>
            <FieldChip doc={ctx.doc} param={type} disabled={!ctx.canWrite} onCommit={(v) => ctx.edit('data.type', v)} changed={ctx.draft?.changes.some((c) => c.path === 'type')} />
          </span>
        )}
      </div>
    </div>
  );
}

function SegmentBody({ ctx }: { ctx: CardContext }) {
  const params = paramsOf(SEGMENT);
  const meta = (ctx.doc.meta ?? {}) as Record<string, unknown>;
  const description = typeof meta.description === 'string' ? meta.description : '';
  return (
    <div className="card-fields lo-body solo">
      <div className="card-fields-main">
        <Group title="Segment">
          <BodyField ctx={ctx} params={params} name="name" />
          <Field label="Description">
            <CommitTextInput value={description} multiline placeholder="Add a description…" disabled={!ctx.canWrite} aria-label="Description" onCommit={(v) => ctx.edit('meta', v.trim() ? { ...meta, description: v } : Object.fromEntries(Object.entries(meta).filter(([k]) => k !== 'description')))} />
          </Field>
        </Group>
        <Group title="Condition" single>
          <div className="pane">
            <div className="pane-body">
              <BodyField ctx={ctx} params={params} name="condition" />
            </div>
          </div>
        </Group>
        <Group title="Notes">
          <CommonTail ctx={ctx} />
        </Group>
      </div>
    </div>
  );
}

/** Entered / left per day over the shared analytics range (the analytics events, beside M6's sizes from the profiles). */
function SegmentFlow({ id }: { id: string }) {
  const f = useEntityFigures('segment', [id]);
  const define = useDefinitionText();
  const item = f.get(id);
  if (!item) return null;
  const entered = item.days.map((d) => n(d.entered));
  const left = item.days.map((d) => n(d.left));
  const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
  const title = define(f.definitions, ['entered', 'testers'], f.computedAt, [`${f.from} → ${f.to}: ${sum(entered)} entered, ${sum(left)} left (the analytics events; the sizes beside come from the profiles)`]);
  return (
    <span className="row segment-flow" data-testid={`segment-flow-${id}`} title={title}>
      <Sparkline values={entered} second={left} label={`Entered per day ${entered.join(', ')}; left per day ${left.join(', ')}`} title={title} />
      <span className="muted small">
        +{sum(entered)} / −{sum(left)}
      </span>
    </span>
  );
}
